import axios from 'axios';
import Cookies from 'js-cookie';
import {backendUrl, backendUrlV2} from '~/config';
import templateTypes from '../static/javascript/nano/templateTypes.json';
import {initThemeOverrideFromURLParams} from './default-theme';

class APIInstance {
  phceId = '';
  globalDefaultTheme = null;

  setAuthorizationCookie = token => {
    Cookies.set('identity_login_attempted', 0);
    Cookies.set('tg-visit', token);
  };

  setPhceId = phce => {
    this.phceId = phce;
  };

  configureRequests = ({courseId}) => {
    this.courseId = courseId;

    const globalDefaultTheme = initThemeOverrideFromURLParams();

    if (globalDefaultTheme) {
      this.globalDefaultTheme = globalDefaultTheme;
    }

    if (process.env.AUTH_TOKEN) {
      this.setAuthorizationCookie(process.env.AUTH_TOKEN);
    }
    // axios.defaults.baseURL = backendUrl;
    axios.defaults.withCredentials = true;
    axios.defaults.crossdomain = true;
  };

  getEditorData = () => {
    if (window.previewMode) {
      return {};
    }

    return axios.get(`${backendUrlV2}/editor/${this.courseId}/data`)
      .then(({data}) => {
        if (!data?.courseData) return data;

        // If we have a global default theme set from the URL params, apply it to the course data if it doesn't have a theme set.
        // Should we do this only if the course data is empty? (no slides yet)
        if (this.globalDefaultTheme && !data.courseData?.settings?.theme?.color) {
          if (!data.courseData?.settings) {
            data.courseData.settings = {};
          }

          const {
            color,
            colorTo,
            defaultFont,
            defaultFontTitle,
            txtColor,
          } = this.globalDefaultTheme;

          data.courseData.settings.theme = {
            color,
            colorTo,
            defaultFont,
            defaultFontTitle,
            txtColor,
          };
        }

        data.courseData = {
          descriptionPage: {},
          resultsPage: {disabled: true},
          ...data.courseData,
        };

        return data;
      })
      .catch(error => {
        console.log('error');
        console.log(error);
      });
  };

  useToken = token => axios.get(`${backendUrlV2}/user/logintoken/${token}`).then(({data}) => data);

  getScormData = courseId => axios.get(`./data/${courseId}`).then(({data}) => data);

  getTrackData = () => axios.get('./data/track.json').then(({data}) => data);

  getPlayerData = courseId => {
    if (window.previewMode) {
      return {};
    }

    return axios.get(`${backendUrl}/player/data/${courseId}`).then(({data}) => data);
  };

  getTemplateTypes = () => ({data: templateTypes});

  postCourse = data => axios.post(`${backendUrlV2}/editor/${this.courseId}/data`, {
    cid: this.courseId,
    data,
  });

  postCourseDraft = data =>
    axios.post(`${backendUrlV2}/editor/${this.courseId}/draft`, {
      cid: this.courseId,
      data,
    });

  postEvent = (type, pageId, data) => {
    if (window.previewMode) {
      return {};
    }

    if (type === 'bulk') {
      return axios.post(`${backendUrl}/api/player/${this.courseId}/event`, {
        type,
        pageId,
        phce: this.phceId,
        events: data,
      });
    }

    return axios
      .post(`${backendUrl}/api/player/${this.courseId}/event`, {
        type,
        pageId,
        phce: this.phceId,
        data,
      })
      .then(response => {
        const phceId = response.data?.phce?.id;

        if (phceId) {
          this.phceId = phceId;
        }

        return response;
      });
  };

  getFiles = params =>
    axios.get(`${backendUrl}/files/getresources`, {
      withCredentials: false,
      params,
    });

  uploadFile = async file => {
    if (window.previewMode) {
      return {};
    }

    const formData = new FormData();

    formData.append('file', file);
    formData.append('timestamp', Math.round(Date.now() / 1000));
    const {type: mimetype} = file;

    const response = await axios
      .post(`${backendUrl}/files/savemanagefiles/nano/${this.courseId}`, formData, {headers: {'X-Requested-With': 'XMLHttpRequest'}});

    // Checking type and modifying response should be done on the server side instead of here...

    response.data.mimetype = mimetype;

    if (mimetype.startsWith('video/')) {
      response.data.type = 'video';
    } else if (mimetype.startsWith('image/')) {
      response.data.type = 'image';
    }

    return response;
  };

  getConfig = target => {
    if (window.previewMode || process.env.REACT_APP_TYPE === 'scorm') {
      return {};
    }

    const lang = localStorage.getItem('language') || 'no';

    return axios.get(`${backendUrl}/templates/config/nano_${target}.json?lang=${lang}`).then(response => {
      this.config = response.data;

      return response.data;
    });
  };

  putCourseData = (cid, data) => axios.put(`${backendUrlV2}/competences/${cid}`, data).then(response => response.data);
}

export const API = new APIInstance();
